import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ReactComponent as Export } from '$icons/export.svg';
import SwitchTypes from '../../constants/CandidatesSwitchTypes';
import MatchTypes from '../../constants/MatchValues';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ReactComponent as BackArrow } from '../../icons/ArrowBackInternal.svg';
import { ReactComponent as RefreshIcon } from '../../icons/RefreshIcon.svg';
import {
  candidatesApi,
  // useGetCandidatesByPositionIdQuery,
  useLazyFindCandidateQuery,
} from '../../store/modules/candidates';
import { useLoadOnePositionQuery } from '../../store/modules/companies';
import { CandidateData } from '../../types/candidates';
import CandidatesSearchFilters from '../../types/candidatesPaginatedSearch';
import Button from '../Buttons/Button';
import CandidatesSwitch from '../CandidatesSwitch/CandidatesSwitch';
import { Filters } from '../Internal/Positions/Filters';
import CandidateSummaryCardListGraph from './CandidateSummaryCardList/CandidateSummaryCardListGraph';
import CardSkeleton from './CandidateSummaryCardList/CardSkeleton/CardSkeleton';
import SearchBar from './SearchBar/SearchBar';
import classes from './SinglePosition.module.css';
import SinglePositionCard from './SinglePositionCard';
import GetCandidateDataByMultipleFetch from './utils/GetCandidateDataByMultipleFetch';
import SearchFilter from './utils/SearchFilter';
import AddCandidatesIntoPosition from './AddCandidatesIntoPosition/AddCandidatesIntoPosition';

const SinglePositionPage: FC = () => {
  // const [data, setData] = useState<any>();
  const { id: positionId } = useParams();
  const [candidates, setCandidates] = useState<CandidateData[]>([]);
  const [positionCandidates, setPositionCandidates] = useState<CandidateData[]>(
    []
  );
  const [findCandidatesResult, setFindCandidatesResult] = useState<
    CandidateData[]
  >([]);
  const [t] = useTranslation('global');
  const [searchParams] = useSearchParams();
  const [candidatesPositionCount, setCandidatesPositionCount] = useState({
    new: 0,
    shortlisted: 0,
    declined: 0,
    requestInterview: 0,
    hired: 0,
    offerSent: 0,
  });
  const [switchingCandidateId, setSwitchingCandidateId] = useState<
    number | null
  >(null);

  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [filteredData, setFilteredData] = useState(candidates);
  const lastSearchTotalItems = useRef(0);

  const navigate = useNavigate();
  const cps = (searchParams.get('cps') ?? SwitchTypes.ACTIVE) as SwitchTypes;

  const { data: position } = useLoadOnePositionQuery({
    id: positionId,
  });

  // const {
  //   // data: candidatesApi,
  //   isLoading,
  //   isFetching,
  //   refetch,
  // } = useGetCandidatesByPositionIdQuery(
  //   {
  //     positionId: parseInt(positionId!, 10),
  //   },
  //   { refetchOnMountOrArgChange: true }
  // );

  const { data, isLoading, refetch } =
    GetCandidateDataByMultipleFetch(position);

  const [findCandidates] = useLazyFindCandidateQuery();

  const dispatch = useAppDispatch();
  const { Country: selectedCountryFilter } = useAppSelector(
    (state) => state.internalFilter
  );

  const filters = useMemo((): CandidatesSearchFilters => {
    const yearsOfExperienceQuery = searchParams.get('YearsOfExperience') || '0';
    const yearsOfExperience = parseInt(yearsOfExperienceQuery, 10) || null;

    const maxSalaryQuery = searchParams.get('MaxSalary') || '0';
    const maxSalary = parseInt(maxSalaryQuery, 10) || null;

    setPage(1);
    setHasNextPage(false);
    return {
      cps,
      matchSkill: searchParams.get('SkillsMatch') ?? MatchTypes.ALL,
      matchAdditionalTags:
        searchParams.get('AdditionalTagsMatch') ?? MatchTypes.ALL,
      additionalTags: searchParams.get('AdditionalTags')?.split(','),
      education: searchParams.get('EducationLevel')?.split(','),
      fileCategories: searchParams.get('Files')?.split(','),
      language: searchParams.get('Language'),
      skills: searchParams.get('Skills')?.split(','),
      englishLevel: searchParams.get('EnglishLevel')?.split(','),
      positionId: parseInt(positionId ?? '0', 10),
      id:
        parseInt(searchParams.get('Id') ?? '0', 10) === 0
          ? null
          : parseInt(searchParams.get('Id') ?? '0', 10),
      workExperience: searchParams.get('PreviousExperience')?.split(','),
      maxSalary,
      query: searchParams.get('Query') ?? '',
      yearsOfExperience,
      name: searchParams.get('Name') ?? null,
      email: searchParams.get('Email') ?? null,
      country: selectedCountryFilter,
    };
  }, [cps, positionId, searchParams, selectedCountryFilter]);

  const reloadCandidates = useCallback(() => {
    dispatch(candidatesApi.util.resetApiState());
    setCandidates([]);
    setPage(1);
    refetch();
  }, [dispatch, refetch]);

  const searchCandidate = useCallback(
    async (findParams) => {
      setIsLoadingSearch(true);
      findCandidates(findParams)
        .then((findCandidateData) => {
          setIsLoadingSearch(false);
          setHasNextPage(!!findCandidateData?.data?.next);
          setFindCandidatesResult((prev) => {
            const resultData = findCandidateData?.data?.data || [];
            return [...prev, ...resultData].reduce(
              (previous: CandidateData[], actual: CandidateData) => {
                const isNewCandidate = !previous.some(
                  (singleCandidate: CandidateData) => {
                    return singleCandidate.Id === actual.Id;
                  }
                );

                if (isNewCandidate) previous.push(actual);
                return previous;
              },
              []
            );
          });
        })
        .catch(() => {
          setIsLoadingSearch(false);
        });
    },
    [findCandidates]
  );

  const onNextPage = useCallback(() => {
    if (hasNextPage && !isLoadingSearch) {
      setPage((prev) => prev + 1);
    }
  }, [hasNextPage, isLoadingSearch]);

  const exportCandidates = useCallback(() => {
    const headers = [
      'Candidate Id',
      'Candidate URL',
      'Email',
      'Name',
      'Last Name',
      'Salary Range',
      'CV',
      'Video',
      'Reference',
    ].join(',');
    const csvContent = filteredData.map((singleCandidate) => {
      const candidateData: string[] = [];
      /* Candidate Id */
      candidateData.push(`${singleCandidate.Id}`);
      /* Candidate URL */
      const candidatePath = `/simera-dashboard/positions/${positionId}/candidates/${singleCandidate.Id}`;
      const candidateUrl = new URL(candidatePath, window.location.origin);
      candidateData.push(candidateUrl.toString());
      /* Candidate Email */
      candidateData.push(`"${singleCandidate.User?.Email}"`);
      /* Candidate Name */
      candidateData.push(`"${singleCandidate.User?.FirstName}"`);
      /* Candidate Last Name */
      candidateData.push(`"${singleCandidate.User?.LastName}"`);
      /* Candidate Salary Range */
      if (
        !singleCandidate.CandidateJobInterest ||
        (!singleCandidate.CandidateJobInterest?.SalaryLowerBound &&
          !singleCandidate.CandidateJobInterest?.SalaryUpperBound)
      )
        candidateData.push('N/A');
      else
        candidateData.push(
          `${
            singleCandidate.CandidateJobInterest.SalaryLowerBound || 'N/A'
          } - ${singleCandidate.CandidateJobInterest.SalaryUpperBound || 'N/A'}`
        );
      /* CV */
      candidateData.push(singleCandidate.Curriculum?.Url ? 'TRUE' : 'FALSE');
      /* Video */
      candidateData.push(
        singleCandidate.AdditionalVideos &&
          singleCandidate.AdditionalVideos.length > 0
          ? 'TRUE'
          : 'FALSE'
      );
      /* Reference */
      candidateData.push(
        singleCandidate.Files &&
          singleCandidate.Files.length > 0 &&
          singleCandidate.Files.find(
            (singleFile) =>
              singleFile?.FileCategory?.CategoryName?.toLowerCase() ===
              'reference'
          )
          ? 'TRUE'
          : 'FALSE'
      );
      return candidateData.join(',');
    });

    const blob = new Blob([[headers, ...csvContent].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = 'Simera - Candidates.csv';

    link.click();

    URL.revokeObjectURL(blobUrl);
  }, [filteredData, positionId]);

  const applyFilters = useMemo(() => {
    // reset lastSearchTotalItems, 25 its the default return value for the first page, if its less it should be a new search
    if (candidates.length <= 25) lastSearchTotalItems.current = 0;
    return SearchFilter(filters, candidates);
  }, [filters, candidates]);

  useEffect(() => {
    setFilteredData(applyFilters);
  }, [applyFilters]);

  useEffect(() => {
    if (data && !isLoading) {
      setPositionCandidates((prev) => {
        const newCandidates = data?.filter(
          (item: CandidateData) =>
            !prev.some((prevItem) => prevItem.Id === item.Id)
        );
        const parsePrev = prev.filter(
          (item) => item.Id !== switchingCandidateId
        );
        return [...parsePrev, ...newCandidates];
      });
    }
  }, [data, isLoading, switchingCandidateId, cps]);

  useEffect(() => {
    if (cps === 'find') setCandidates(findCandidatesResult);
    else setCandidates(positionCandidates);
  }, [cps, positionCandidates, findCandidatesResult]);

  useEffect(() => {
    const findParamsLength = Array.from(searchParams.keys()).length;
    if (cps.toLowerCase() === 'find') {
      if (findParamsLength > 1) {
        searchCandidate({
          ...Object.fromEntries(searchParams.entries()),
          page,
        });
      } else {
        setCandidates([]);
      }
    } else {
      setCandidates(positionCandidates);
    }
  }, [cps, searchParams, positionCandidates, searchCandidate, page]);

  useEffect(() => {
    if (position?.CandidatesPositionStatus) {
      const candidatesCount = position?.CandidatesPositionStatus?.reduce(
        (acc, curr) => {
          acc[curr.Status] += 1;

          return acc;
        },
        {
          new: 0,
          shortlisted: 0,
          declined: 0,
          requestInterview: 0,
          hired: 0,
          offerSent: 0,
        }
      );
      setCandidatesPositionCount(candidatesCount);
    }
  }, [position?.CandidatesPositionStatus]);

  useEffect(() => {
    if (!(isLoading || isLoadingSearch)) {
      if (filteredData.length < 3) {
        // 3 items to make virtuoso have scroll bar and enable atBottomStateChange trigger
        onNextPage();
      } else if (
        // save last filtered result
        lastSearchTotalItems.current === 0 ||
        lastSearchTotalItems.current !== filteredData.length
      ) {
        lastSearchTotalItems.current = filteredData.length;
      } else if (lastSearchTotalItems.current === filteredData.length) {
        // if parent component sent new data and the new whole set didnt aggregate items, call again next page
        onNextPage();
      }
    }
  }, [filteredData.length, isLoading, isLoadingSearch, onNextPage]);

  return (
    <div className={classes.PositionWrapper} data-testid="positionWrapper">
      <Button
        text={t('CandidatesTable.Previous')}
        showText
        customStyles={{
          color: '#079E88',
          width: '190px',
          alignSelf: 'flex-start',
          border: '1px solid #079E88',
          borderRadius: '4px',
          backgroundColor: 'transparent',
          padding: '0.5rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          justifyContent: 'flex-start',
        }}
        textStyles={{
          marginLeft: '30px',
        }}
        icon={BackArrow}
        iconStyles={{ fill: '#079E88' }}
        onClick={() => navigate('/internal-dashboard', { replace: true })}
      />
      <div className={classes.Container}>
        <Filters />
        <div className={classes.PositionMainContainer}>
          <div className={classes.PositionContainer}>
            <SinglePositionCard
              candidatesCount={position?.CandidatesPositionStatus?.length ?? 0}
            />
          </div>
          <div className={classes.PositionCandidatesContainer}>
            <div className={classes.PositionCandidatesFilter}>
              <CandidatesSwitch
                value={(cps as SwitchTypes) ?? SwitchTypes.ACTIVE}
                setSwitchingId={setSwitchingCandidateId}
                candidatesCount={candidatesPositionCount}
              />
            </div>
            <div className={classes.PositionCandidatesSearch}>
              <SearchBar />
            </div>
            <AddCandidatesIntoPosition positionId={positionId as string} />
            <div
              className={classes.refresh}
              aria-hidden="true"
              onClick={reloadCandidates}
            >
              <RefreshIcon />
            </div>
            <button
              type="button"
              className={classes.refresh}
              title="Export"
              onClick={exportCandidates}
            >
              <Export className="fill-white w-5 rotate-180" />
            </button>
          </div>

          {isLoading ||
          (cps === 'find' && isLoadingSearch && candidates.length === 0) ? (
            <div data-testid="loading-page1">
              <CardSkeleton />
            </div>
          ) : (
            <CandidateSummaryCardListGraph
              loading={isLoading || isLoadingSearch}
              jobPositionStatus={cps}
              candidatesData={filteredData}
              positionId={parseInt(positionId ?? '0', 10)}
              currentFilter={cps}
              setSwitchingId={setSwitchingCandidateId}
              onNextPage={onNextPage}
              filters={filters}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SinglePositionPage;
